import { http, toQuery } from '@/lib/apiClient';
import type { WorkflowInstance, WorkflowTask } from '@/api/workflows.api';
import type { LifecycleTimelineStage } from '@/api/lifecycleTimeline';
import type { VendorInvoice } from '@/api/vendorInvoices.api';
import type { AdvanceRecord } from '@/api/finance.api';

export type PurchaseOrderStatus = 'UPLOADED' | 'PENDING_APPROVAL' | 'SENT_TO_VENDOR' | 'ACCEPTED' | 'PARTIALLY_RECEIVED' | 'RECEIVED' | 'REJECTED_BY_VENDOR' | 'REJECTED';
export interface PurchaseOrderQuery { page?: number; pageSize?: number; status?: PurchaseOrderStatus; search?: string; vendorId?: string }
export interface PurchaseOrderAttachment { id?: string; fileName?: string | null; mimeType?: string | null; fileSize?: number | null; downloadUrl: string }
export interface PurchaseOrderMessage { id: string; poId?: string; message: string; senderUserId?: string | null; senderName?: string | null; senderUserType?: string | null; createdByName?: string | null; createdAt: string; isVendorMessage?: boolean }
export interface PurchaseOrderSummary {
  id: string; poNumber: string; status: PurchaseOrderStatus; vendorId: string; vendorName: string; createdAt: string;
  approvedAt?: string | null; sentToVendorAt?: string | null; acceptedAt?: string | null; remarks?: string | null;
  approvedByName?: string | null; amount?: number | null; totalAmount?: number | null; currencyCode?: string | null;
}
export interface PurchaseOrderDetail extends PurchaseOrderSummary { attachments: PurchaseOrderAttachment[]; recentMessages: PurchaseOrderMessage[]; invoices?: VendorInvoice[] | null; advances?: AdvanceRecord[] | null; lifecycleTimeline?: LifecycleTimelineStage[] | null }
export interface PurchaseOrderPreview {
  id?: string;
  fileName?: string | null;
  mimeType?: string | null;
  previewUrl?: string | null;
  downloadUrl?: string | null;
  documentUrl?: string | null;
}
export interface PurchaseOrderApprovalTaskDetail extends Partial<WorkflowTask> {
  task?: WorkflowTask;
  workflowTask?: WorkflowTask;
  currentTask?: WorkflowTask;
  instance?: WorkflowInstance;
  workflowInstance?: WorkflowInstance;
  purchaseOrder?: PurchaseOrderDetail;
  po?: PurchaseOrderDetail;
  attachments?: PurchaseOrderPreview[];
  documentPreviewUrls?: Array<PurchaseOrderPreview | string>;
}
export interface PagedPurchaseOrders { items: PurchaseOrderSummary[]; page: number; pageSize: number; totalCount: number }
export type PurchaseOrderMessagesResponse = PurchaseOrderMessage[] | { items?: PurchaseOrderMessage[]; messages?: PurchaseOrderMessage[] };

export const purchaseOrdersApi = {
  list: (query: PurchaseOrderQuery = {}) => http.get<PagedPurchaseOrders>(`/api/v1/purchase-orders${toQuery({ page: query.page ?? 1, pageSize: query.pageSize ?? 20, status: query.status, search: query.search, vendorId: query.vendorId })}`),
  getById: (id: string) => http.get<PurchaseOrderDetail>(`/api/v1/purchase-orders/${id}`),
  getApprovalTask: (taskId: string) => http.get<PurchaseOrderApprovalTaskDetail>(`/api/v1/purchase-orders/approval-tasks/${taskId}`),
  upload: (input: { vendorId: string; remarks?: string; file: File }) => {
    const form = new FormData();
    form.append('vendorId', input.vendorId);
    if (input.remarks?.trim()) form.append('remarks', input.remarks.trim());
    form.append('file', input.file);
    return http.post<PurchaseOrderDetail>('/api/v1/purchase-orders', form);
  },
  submit: (id: string) => http.post<PurchaseOrderDetail>(`/api/v1/purchase-orders/${id}/submit`),
  accept: (id: string) => http.post<PurchaseOrderDetail>(`/api/v1/purchase-orders/${id}/accept`, { remarks: null }),
  reject: (id: string, remarks: string) => http.post<PurchaseOrderDetail>(`/api/v1/purchase-orders/${id}/reject`, { remarks }),
  messages: (id: string) => http.get<PurchaseOrderMessagesResponse>(`/api/v1/purchase-orders/${id}/messages`),
  addMessage: (id: string, message: string) => http.post<PurchaseOrderMessage>(`/api/v1/purchase-orders/${id}/messages`, { message }),
  download: (url: string) => http.blob(url),
};
